import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { KpiQueryParams, UserSegment as UserSegmentType } from '../../types/kpi.types';
import { BarChart, MetricCard } from '../Charts';
import { Loader } from '../Loader';

interface UserSegmentProps {
  queryParams: KpiQueryParams;
}

export const UserSegment: React.FC<UserSegmentProps> = ({
  queryParams,
}) => {
  const [segments, setSegments] = useState<UserSegmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await analyticsService.getUserSegments(queryParams);
        setSegments(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load user segment data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.startDate, queryParams.endDate, queryParams.companyId, queryParams.database]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  if (!segments || segments.length === 0) {
    return <div>No data available</div>;
  }

  // Filter out segments with 0 users
  const validSegments = segments.filter(s => s.userCount > 0);

  // Calculate totals for summary metrics
  const totalUsers = validSegments.reduce((sum, s) => sum + s.userCount, 0);

  // User count distribution chart
  const userCountChartData = validSegments.map(s => ({
    segment: s.daysRange,
    users: s.userCount,
  }));

  // Retention rate chart
  const retentionChartData = validSegments.map(s => ({
    segment: s.daysRange,
    retention: s.percentageRetained,
  }));


  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          👥 User Segments
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Segment users by time to first transfer and analyze transfer behavior patterns. All data shown is for active users only (users with shifts in the last 2 months).
        </p>
      </div>

      {/* Summary Metrics */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <MetricCard
          title="Total Users"
          value={totalUsers.toLocaleString()}
          subtitle="Across all segments"
          color="#4CAF50"
        />
      </div>

      {/* Charts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <BarChart
          data={userCountChartData}
          title="User Count by Segment"
          dataKey="users"
          xAxisKey="segment"
          color="#4CAF50"
          height={300}
        />
        <BarChart
          data={retentionChartData}
          title="Retention Rate by Segment"
          dataKey="retention"
          xAxisKey="segment"
          color="#ffc658"
          height={300}
        />
      </div>

      {/* Detailed Table */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '24px',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
          📊 Segment Details
        </h3>
        
        {/* Explanation Box */}
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#555',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>📖 Understanding User Segments:</div>
          <div style={{ lineHeight: '1.8' }}>
            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #ddd' }}>
              <strong>Active Users Only:</strong> All data shown in this breakdown is for active users only - users who have had shifts in the last 2 months (as of 15/12/2025).
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong>Days to First Transfer:</strong> Time from user registration to their first successful transfer.
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong>Avg Amount Transferred:</strong> Average total amount transferred per user (all successful transfers combined).
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong>Median/Avg Days Between Transfers:</strong> Time between consecutive transfers (calculated only for users with 2+ transfers).
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong>% Single Transfer:</strong> Percentage of users in this segment who made only 1 transfer.
            </div>
            <div>
              <strong>% Retained in Payro:</strong> Percentage of users who transferred within the last 3 months (90 days) as of 15/12/2025. Users who haven't transferred for more than 3 months (90+ days) since their last transfer are classified as "left Payro" (not retained).
            </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333' }}>Days to First Transfer</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Number of Users</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Avg Total Amount Transferred</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Avg Transfers per User</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Median Days Between Transfers</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>Avg Days Between Transfers</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>% Users with Only 1 Transfer</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>% Retained in Payro</th>
              </tr>
            </thead>
            <tbody>
              {validSegments.map((segment, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', color: '#333', fontWeight: 500 }}>
                    {segment.daysRange}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                    {segment.userCount.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                    ₪{(segment.avgAmountTransferred || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                    {(segment.avgTransfersPerUser || 0).toFixed(1)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#333' }}>
                    {(segment.medianDaysBetweenTransfers || 0) > 0 
                      ? (segment.medianDaysBetweenTransfers || 0).toFixed(1)
                      : '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#333' }}>
                    {(segment.avgDaysBetweenTransfers || 0) > 0 
                      ? (segment.avgDaysBetweenTransfers || 0).toFixed(1)
                      : '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#333', fontWeight: 500 }}>
                    {(segment.percentageSingleTransfer || 0).toFixed(1)}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#4CAF50', fontWeight: 500 }}>
                    {(segment.percentageRetained || 0).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


